/**
 * FB Marketplace Bridge — Posts listings via THREE paths:
 *
 * 1. DESKTOP: Chrome extension (FlipFresh) via chrome.runtime.sendMessage
 *    - Fully automated: token extraction → photo upload → GraphQL create
 *    - Shield anti-detection (Gaussian delays, rate limits, ban detection)
 *
 * 2. NATIVE MOBILE: In-app WebView (@capgo/inappbrowser)
 *    - Opens facebook.com in a hidden WebView
 *    - Extracts FB session tokens via executeScript()
 *    - Uploads photos + creates listing INSIDE the WebView context (no CORS)
 *    - Humanized Gaussian delays between all operations
 *    - Full automation — user just taps one button
 *
 * 3. FALLBACK (mobile web/desktop no extension): Assisted posting
 *    - Copies listing to clipboard + opens FB Marketplace create page
 *
 * Feature: Dealer-only (available during 7-day trial).
 */

import { Capacitor } from '@capacitor/core';
import { api } from './api-client.js';

const EXTENSION_ID = (window as any).__ACP_EXTENSION_ID || 'YOUR_EXTENSION_ID';
declare const chrome: any;

// ── Types ──

export interface MarketplacePostData {
  vehicle_id: string;
  title: string;
  price: number;
  description: string;
  photos: string[];
  vehicle: {
    year: number | null;
    make: string;
    model: string;
    trim: string;
    mileage: number;
    transmission: string | null;
    fuel_type: string | null;
    body_style: string | null;
    exterior_color: string | null;
    drivetrain: string | null;
    vin: string | null;
  };
  location: { city: string; state: string; zip: string } | null;
}

export interface PostResult {
  ok: boolean;
  listingId?: string;
  listingUrl?: string;
  error?: string;
  method?: 'extension' | 'native' | 'assisted';
  banSignals?: string[];
}

// ── Helpers ──

function gaussian(mean: number, stddev: number): number {
  let u = 0, v = 0;
  while (u === 0) u = Math.random();
  while (v === 0) v = Math.random();
  const z = Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v);
  return Math.max(mean * 0.3, mean + z * stddev);
}

function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

// ── Rate limiter (native path) ──

const RATE_KEY = 'acp_fb_rate';
function checkRate(): { ok: boolean; reason?: string } {
  try {
    const raw = JSON.parse(localStorage.getItem(RATE_KEY) || '[]') as number[];
    const now = Date.now();
    const hourAgo = now - 3600000;
    const recent = raw.filter(t => t > hourAgo);
    if (recent.length >= 10) return { ok: false, reason: `Rate limit: ${recent.length}/10 this hour. Wait a bit.` };
    return { ok: true };
  } catch { return { ok: true }; }
}
function recordRate(): void {
  try {
    const raw = JSON.parse(localStorage.getItem(RATE_KEY) || '[]') as number[];
    raw.push(Date.now());
    localStorage.setItem(RATE_KEY, JSON.stringify(raw.slice(-100)));
  } catch { /* best effort */ }
}

// ── Detection ──

export type PostMethod = 'extension' | 'native' | 'assisted';

export async function detectPostMethod(): Promise<PostMethod> {
  // Desktop Chrome with extension?
  if (typeof chrome !== 'undefined' && chrome?.runtime?.sendMessage) {
    try {
      const ok = await new Promise<boolean>(resolve => {
        chrome.runtime.sendMessage(EXTENSION_ID, { type: 'ACP_PING' }, (r: any) => {
          if (chrome.runtime.lastError) resolve(false);
          else resolve(!!r?.ok);
        });
      });
      if (ok) return 'extension';
    } catch { /* fall through */ }
  }

  // Native Capacitor app with InAppBrowser?
  if (Capacitor.isNativePlatform()) {
    return 'native';
  }

  // Web fallback
  return 'assisted';
}

// ═══════════════════════════════════════════════
// PATH 1: DESKTOP — Chrome extension
// ═══════════════════════════════════════════════

async function postViaExtension(data: MarketplacePostData): Promise<PostResult> {
  return new Promise(resolve => {
    chrome.runtime.sendMessage(EXTENSION_ID, {
      type: 'ACP_CREATE_LISTING',
      data,
    }, (response: any) => {
      if (chrome.runtime.lastError) {
        resolve({ ok: false, error: 'Extension not responding. Open facebook.com and try again.', method: 'extension' });
        return;
      }
      resolve({ ...(response || { ok: false, error: 'No response' }), method: 'extension' });
    });
  });
}

// ═══════════════════════════════════════════════
// PATH 2: NATIVE MOBILE — WebView automation
// ═══════════════════════════════════════════════

/**
 * The complete native posting flow:
 * 1. Open hidden WebView to facebook.com/marketplace
 * 2. Wait for page load
 * 3. Extract tokens (fb_dtsg, user_id, jazoest, etc.)
 * 4. If not logged in → show WebView → user logs in → re-extract
 * 5. For each photo: download → upload to FB CDN (inside WebView = same origin)
 * 6. Create listing via GraphQL (inside WebView = same origin)
 * 7. Close WebView, return listing URL
 *
 * All FB API calls happen inside the WebView context —
 * the WebView IS a real FB browser session. No CORS issues.
 */
async function postViaNative(data: MarketplacePostData): Promise<PostResult> {
  // Rate limit
  const rate = checkRate();
  if (!rate.ok) return { ok: false, error: rate.reason, method: 'native' };

  let IAB: any;
  try {
    IAB = (await import('@capgo/inappbrowser')).InAppBrowser;
  } catch {
    // Plugin not available — fall back to assisted
    return postViaAssisted(data);
  }

  try {
    // Open WebView to Facebook (hidden initially)
    await IAB.openWebView({
      url: 'https://www.facebook.com/marketplace/',
      isPresentAfterPageLoad: false, // hidden until we need to show it
      preventDeeplink: true,
      title: 'Posting to Marketplace...',
      toolbarType: 'activity',
    });

    // Wait for page to load
    await waitForPageLoad(IAB);
    await delay(gaussian(2000, 500));

    // Extract tokens
    let tokens = await extractTokens(IAB);

    // If not logged in, show the WebView for user to log in
    if (!tokens.fb_dtsg || !tokens.user_id) {
      await IAB.show();
      // Wait for user to log in (poll for tokens)
      tokens = await waitForLogin(IAB);
      if (!tokens.fb_dtsg) {
        await IAB.close();
        return { ok: false, error: 'Facebook login required. Please log in and try again.', method: 'native' };
      }
      // Navigate to marketplace after login
      await IAB.setUrl({ url: 'https://www.facebook.com/marketplace/' });
      await waitForPageLoad(IAB);
      await delay(gaussian(2000, 500));
      // Re-extract tokens from marketplace page
      tokens = await extractTokens(IAB);
      await IAB.hide();
    }

    // Humanized ramp-up delay
    await delay(gaussian(2500, 800));

    // Upload photos (inside WebView context — no CORS)
    const photoIds: string[] = [];
    for (let i = 0; i < data.photos.length; i++) {
      const photoId = await uploadPhotoInWebView(IAB, data.photos[i], tokens);
      if (photoId) photoIds.push(photoId);
      if (i < data.photos.length - 1) {
        await delay(gaussian(2500, 800)); // Humanized inter-upload delay
      }
    }

    if (photoIds.length === 0) {
      await IAB.close();
      return { ok: false, error: 'Failed to upload photos to Facebook', method: 'native' };
    }

    // Pre-create delay
    await delay(gaussian(4000, 1200));

    // Create listing via GraphQL (inside WebView)
    const result = await createListingInWebView(IAB, data, photoIds, tokens);
    await IAB.close();

    if (result.listingId) {
      recordRate();
      return {
        ok: true,
        listingId: result.listingId,
        listingUrl: `https://www.facebook.com/marketplace/item/${result.listingId}/`,
        method: 'native',
        banSignals: result.banSignals,
      };
    }

    return {
      ok: false,
      error: result.error || 'Listing may have been rejected',
      method: 'native',
      banSignals: result.banSignals,
    };
  } catch (err: any) {
    try { await IAB.close(); } catch { /* */ }
    return { ok: false, error: err.message || 'Native posting failed', method: 'native' };
  }
}

function waitForPageLoad(IAB: any): Promise<void> {
  return new Promise((resolve) => {
    let resolved = false;
    const listener = IAB.addListener('browserPageLoaded', () => {
      if (!resolved) {
        resolved = true;
        listener.then?.((h: any) => h?.remove?.());
        resolve();
      }
    });
    // Timeout after 20s
    setTimeout(() => { if (!resolved) { resolved = true; resolve(); } }, 20000);
  });
}

async function extractTokens(IAB: any): Promise<Record<string, string>> {
  // Inject JavaScript that extracts all FB session tokens from the page
  const code = `
    (function() {
      var t = {};
      var scripts = document.querySelectorAll('script');
      for (var i = 0; i < scripts.length; i++) {
        var text = scripts[i].textContent || '';
        if (!t.fb_dtsg) {
          var m = text.match(/"DTSGInitialData"\\s*,\\s*\\[\\]\\s*,\\s*\\{\\s*"token"\\s*:\\s*"([^"]+)"/);
          if (m) t.fb_dtsg = m[1];
          if (!t.fb_dtsg) { m = text.match(/\\["DTSGInitData",\\[\\],\\{"token":"([^"]+)"/); if (m) t.fb_dtsg = m[1]; }
          if (!t.fb_dtsg) { m = text.match(/\\{"name":"fb_dtsg","value":"([^"]+)"\\}/); if (m) t.fb_dtsg = m[1]; }
          if (!t.fb_dtsg) { m = text.match(/"dtsg"\\s*:\\s*\\{"token"\\s*:\\s*"([^"]+)"/); if (m) t.fb_dtsg = m[1]; }
        }
        if (!t.jazoest) { var jm = text.match(/jazoest[=:]\\s*["']?(\\d+)["']?/); if (jm) t.jazoest = jm[1]; }
        if (!t.lsd) { var lm = text.match(/"LSD"\\s*,\\s*\\[\\]\\s*,\\s*\\{"token"\\s*:\\s*"([^"]+)"/); if (lm) t.lsd = lm[1]; }
        if (!t.spin_r) { var sr = text.match(/"__spin_r"\\s*:\\s*(\\d+)/); if (sr) t.spin_r = sr[1]; }
        if (!t.spin_b) { var sb = text.match(/"__spin_b"\\s*:\\s*"([^"]+)"/); if (sb) t.spin_b = sb[1]; }
        if (!t.__rev) { var rv = text.match(/"server_revision"\\s*:\\s*(\\d+)/) || text.match(/"__rev"\\s*:\\s*(\\d+)/); if (rv) t.__rev = rv[1]; }
      }
      if (!t.fb_dtsg) { var dtsgInput = document.querySelector('input[name="fb_dtsg"]'); if (dtsgInput) t.fb_dtsg = dtsgInput.value; }
      var c = document.cookie.match(/c_user=(\\d+)/);
      t.user_id = c ? c[1] : '';
      // Signal result back to native app
      window.mobileApp.postMessage(JSON.stringify({ type: 'tokens', data: t }));
    })();
  `;

  return new Promise(async (resolve) => {
    let resolved = false;
    const listener = await IAB.addListener('messageFromWebview', (msg: any) => {
      try {
        const parsed = typeof msg.detail === 'string' ? JSON.parse(msg.detail) : msg.detail;
        if (parsed?.type === 'tokens' && !resolved) {
          resolved = true;
          listener?.remove?.();
          resolve(parsed.data || {});
        }
      } catch { /* ignore parse errors */ }
    });

    await IAB.executeScript({ code });

    // Timeout
    setTimeout(() => { if (!resolved) { resolved = true; listener?.remove?.(); resolve({}); } }, 10000);
  });
}

async function waitForLogin(IAB: any): Promise<Record<string, string>> {
  // Poll every 3 seconds for up to 2 minutes
  for (let i = 0; i < 40; i++) {
    await delay(3000);
    const tokens = await extractTokens(IAB);
    if (tokens.fb_dtsg && tokens.user_id) return tokens;
  }
  return {};
}

async function uploadPhotoInWebView(
  IAB: any, photoUrl: string, tokens: Record<string, string>
): Promise<string | null> {
  // Execute inside the WebView: download photo → upload to FB CDN
  const code = `
    (async function() {
      try {
        var res = await fetch('${photoUrl.replace(/'/g, "\\'")}');
        if (!res.ok) { window.mobileApp.postMessage(JSON.stringify({ type: 'upload', photoId: null })); return; }
        var blob = await res.blob();
        var fd = new FormData();
        fd.append('farr', blob, 'photo.jpg');
        fd.append('source', '8');
        fd.append('upload_id', 'acp_' + Date.now() + '_' + Math.random().toString(36).slice(2,6));
        fd.append('__user', '${tokens.user_id || ''}');
        fd.append('fb_dtsg', '${(tokens.fb_dtsg || '').replace(/'/g, "\\'")}');
        ${tokens.jazoest ? `fd.append('jazoest', '${tokens.jazoest}');` : ''}
        ${tokens.lsd ? `fd.append('lsd', '${tokens.lsd}');` : ''}
        fd.append('__a', '1');
        var upRes = await fetch('https://upload.facebook.com/ajax/react_composer/attachments/photo/upload', {
          method: 'POST', body: fd, credentials: 'include'
        });
        var text = await upRes.text();
        var json = JSON.parse(text.replace(/^for\\s*\\(;;\\)\\s*;\\s*/, ''));
        var pid = (json && json.payload) ? (json.payload.photoID || json.payload.fbid) : null;
        window.mobileApp.postMessage(JSON.stringify({ type: 'upload', photoId: pid ? String(pid) : null }));
      } catch(e) {
        window.mobileApp.postMessage(JSON.stringify({ type: 'upload', photoId: null, error: e.message }));
      }
    })();
  `;

  return new Promise(async (resolve) => {
    let resolved = false;
    const listener = await IAB.addListener('messageFromWebview', (msg: any) => {
      try {
        const parsed = typeof msg.detail === 'string' ? JSON.parse(msg.detail) : msg.detail;
        if (parsed?.type === 'upload' && !resolved) {
          resolved = true;
          listener?.remove?.();
          resolve(parsed.photoId || null);
        }
      } catch { /* */ }
    });

    await IAB.executeScript({ code });
    setTimeout(() => { if (!resolved) { resolved = true; listener?.remove?.(); resolve(null); } }, 30000);
  });
}

async function createListingInWebView(
  IAB: any,
  data: MarketplacePostData,
  photoIds: string[],
  tokens: Record<string, string>
): Promise<{ listingId?: string; error?: string; banSignals?: string[] }> {
  // Build SVD and variables inside the WebView
  const svdParts: string[] = [];
  if (data.vehicle.year) svdParts.push(`"year":{"i32":${data.vehicle.year}}`);
  if (data.vehicle.make) svdParts.push(`"make":{"str":"${esc(data.vehicle.make)}"}`);
  if (data.vehicle.model) svdParts.push(`"model":{"str":"${esc(data.vehicle.model)}"}`);
  if (data.vehicle.trim) svdParts.push(`"trim":{"str":"${esc(data.vehicle.trim)}"}`);
  if (data.vehicle.mileage) svdParts.push(`"mileage":{"i32":${data.vehicle.mileage},"unit":"MILES"}`);
  if (data.vehicle.transmission) svdParts.push(`"transmission":{"str":"${esc(data.vehicle.transmission)}"}`);
  if (data.vehicle.fuel_type) svdParts.push(`"fuel_type":{"str":"${esc(data.vehicle.fuel_type)}"}`);
  if (data.vehicle.body_style) svdParts.push(`"body_style":{"str":"${esc(data.vehicle.body_style)}"}`);
  if (data.vehicle.exterior_color) svdParts.push(`"exterior_color":{"str":"${esc(data.vehicle.exterior_color)}"}`);
  if (data.vehicle.drivetrain) svdParts.push(`"drivetrain":{"str":"${esc(data.vehicle.drivetrain)}"}`);
  const svdJson = '{' + svdParts.join(',') + '}';

  const photoListJson = JSON.stringify(photoIds.map(id => ({ photo_id: id })));
  const titleEsc = esc(data.title);
  const descEsc = esc(data.description);
  const zip = data.location?.zip || '';

  const code = `
    (async function() {
      try {
        var variables = {
          input: {
            listing_title: '${titleEsc}',
            listing_price: { amount: '${data.price}', currency: 'USD' },
            listing_category_id: '807311116002614',
            listing_photos: ${photoListJson},
            listing_description: { text: '${descEsc}' },
            ${zip ? `listing_location: { zipcode: '${zip}' },` : ''}
            listing_type: 'ITEM',
            serialized_verticals_data: '${svdJson.replace(/'/g, "\\'")}',
            actor_id: '${tokens.user_id || ''}',
            source: 'marketplace'
          }
        };
        var params = new URLSearchParams();
        params.set('fb_dtsg', '${(tokens.fb_dtsg || '').replace(/'/g, "\\'")}');
        params.set('fb_api_caller_class', 'RelayModern');
        params.set('fb_api_req_friendly_name', 'useCometMarketplaceListingCreateMutation');
        params.set('doc_id', '9551550371629242');
        params.set('variables', JSON.stringify(variables));
        params.set('__user', '${tokens.user_id || ''}');
        params.set('__a', '1');
        ${tokens.jazoest ? `params.set('jazoest', '${tokens.jazoest}');` : ''}
        ${tokens.lsd ? `params.set('lsd', '${tokens.lsd}');` : ''}
        ${tokens.__rev ? `params.set('__rev', '${tokens.__rev}');` : ''}
        params.set('__comet_req', '15');

        var res = await fetch('https://www.facebook.com/api/graphql/', {
          method: 'POST', body: params, credentials: 'include',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded', 'X-FB-Friendly-Name': 'useCometMarketplaceListingCreateMutation' }
        });
        var text = await res.text();

        var banSignals = [];
        var lower = text.toLowerCase();
        ['spam','restricted','policy_violation','rate_limit','temporarily_blocked','checkpoint','suspicious_activity'].forEach(function(p) {
          if (lower.indexOf(p) > -1) banSignals.push(p);
        });

        var idMatch = text.match(/"listing_id"\\s*:\\s*"(\\d+)"/) ||
                      text.match(/"for_sale_item_id"\\s*:\\s*"(\\d+)"/) ||
                      text.match(/"marketplace_listing_id"\\s*:\\s*"(\\d+)"/);

        window.mobileApp.postMessage(JSON.stringify({
          type: 'create',
          listingId: idMatch ? idMatch[1] : null,
          banSignals: banSignals
        }));
      } catch(e) {
        window.mobileApp.postMessage(JSON.stringify({ type: 'create', error: e.message }));
      }
    })();
  `;

  return new Promise(async (resolve) => {
    let resolved = false;
    const listener = await IAB.addListener('messageFromWebview', (msg: any) => {
      try {
        const parsed = typeof msg.detail === 'string' ? JSON.parse(msg.detail) : msg.detail;
        if (parsed?.type === 'create' && !resolved) {
          resolved = true;
          listener?.remove?.();
          resolve({
            listingId: parsed.listingId || undefined,
            error: parsed.error || undefined,
            banSignals: parsed.banSignals?.length ? parsed.banSignals : undefined,
          });
        }
      } catch { /* */ }
    });

    await IAB.executeScript({ code });
    setTimeout(() => { if (!resolved) { resolved = true; listener?.remove?.(); resolve({ error: 'Timeout creating listing' }); } }, 45000);
  });
}

function esc(s: string): string {
  return (s || '').replace(/\\/g, '\\\\').replace(/'/g, "\\'").replace(/\n/g, '\\n').replace(/\r/g, '');
}

// ═══════════════════════════════════════════════
// PATH 3: FALLBACK — Assisted posting (clipboard)
// ═══════════════════════════════════════════════

async function postViaAssisted(data: MarketplacePostData): Promise<PostResult> {
  const mileage = data.vehicle.mileage
    ? `${(data.vehicle.mileage >= 1000 ? Math.round(data.vehicle.mileage / 1000) + 'k' : data.vehicle.mileage)} miles`
    : '';

  const details: string[] = [];
  if (data.vehicle.transmission) details.push(data.vehicle.transmission);
  if (data.vehicle.fuel_type) details.push(data.vehicle.fuel_type);
  if (data.vehicle.drivetrain) details.push(data.vehicle.drivetrain);
  if (data.vehicle.exterior_color) details.push(data.vehicle.exterior_color);
  if (data.vehicle.body_style) details.push(data.vehicle.body_style);

  const clipboardText = [
    data.description || '',
    '',
    details.length ? details.join(' | ') : '',
    mileage ? `Mileage: ${mileage}` : '',
    data.vehicle.vin ? `VIN: ${data.vehicle.vin}` : '',
    '',
    `$${data.price.toLocaleString()}`,
  ].filter(line => line !== undefined).join('\n').trim();

  try {
    await navigator.clipboard.writeText(clipboardText);
  } catch {
    const ta = document.createElement('textarea');
    ta.value = clipboardText;
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
  }

  window.open('https://www.facebook.com/marketplace/create/item/', '_blank');

  return { ok: true, method: 'assisted' };
}

// ═══════════════════════════════════════════════
// MAIN ENTRY POINT
// ═══════════════════════════════════════════════

export async function postToFBMarketplace(vehicleId: string): Promise<PostResult> {
  const res = await api.get<MarketplacePostData>(`/vehicles/${vehicleId}/marketplace-data`);
  if (!res.success || !res.data) {
    return { ok: false, error: res.error || 'Failed to load vehicle data' };
  }

  const method = await detectPostMethod();

  switch (method) {
    case 'extension':
      return postViaExtension(res.data);
    case 'native':
      return postViaNative(res.data);
    case 'assisted':
    default:
      return postViaAssisted(res.data);
  }
}
